import React from 'react';

import { InferenceDetection } from 'files/hooks/useImageInference';

export type CropDetection = {
  kind: string;
  detection: InferenceDetection;
};

export type DetectionCropEntry = {
  blobUrl: string;
  file: File;
};

export function cropKey(kind: string, detectionId: string): string {
  return `${kind}:${detectionId}`;
}

/**
 * Decodes the source image once and produces a cropped blob + blob URL
 * for each detection. Returns a Map keyed by `kind:detection_id`, each
 * entry has both the blobUrl (for <img src>) and the File (for upload /
 * onSelect) so callers never have to re-decode the source. The kind
 * prefix prevents ID collisions between the person and face models.
 * Blob URLs are revoked automatically when inputs change or on unmount.
 */
export function useDetectionCrops(
  sourceImageUrl: string | null,
  detections: CropDetection[]
): Map<string, DetectionCropEntry> {
  const [crops, setCrops] = React.useState<Map<string, DetectionCropEntry>>(
    new Map()
  );
  const blobUrlsRef = React.useRef<string[]>([]);

  React.useEffect(() => {
    if (!sourceImageUrl || detections.length === 0) {
      setCrops(new Map());
      return;
    }

    let cancelled = false;

    const img = new Image();
    img.onload = async () => {
      const entries: [string, DetectionCropEntry][] = [];

      for (const { kind, detection } of detections) {
        if (cancelled) break;

        const [x1, y1, x2, y2] = detection.bbox;
        const cropW = Math.max(1, x2 - x1);
        const cropH = Math.max(1, y2 - y1);
        const canvas = document.createElement('canvas');
        canvas.width = cropW;
        canvas.height = cropH;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        ctx.drawImage(img, x1, y1, cropW, cropH, 0, 0, cropW, cropH);

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve)
        );

        if (blob && !cancelled) {
          const blobUrl = URL.createObjectURL(blob);
          blobUrlsRef.current.push(blobUrl);
          const file = new File([blob], `${kind}-crop.png`, {
            type: blob.type || 'image/png',
          });
          entries.push([cropKey(kind, detection.detection_id), { blobUrl, file }]);
        }
      }

      if (!cancelled) {
        setCrops(new Map(entries));
      }
    };
    img.src = sourceImageUrl;

    return () => {
      cancelled = true;
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
      blobUrlsRef.current = [];
    };
  }, [sourceImageUrl, detections]);

  return crops;
}
